import { faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { FormattedMessage, FormattedNumber, defineMessages } from 'react-intl';
import { useNavigate } from 'react-router';
import styled from 'styled-components';

import { Link } from '@sorare/routing';

import {
  InGameCurrency,
  Rarity,
  Sport,
} from '#src/__generated__/globalTypes.ts';
import { Button } from '#src/atoms/buttons/Button/index.tsx';
import { Boost } from '#src/atoms/icons/Boost.tsx';
import { Energy } from '#src/atoms/icons/Energy.tsx';
import { GemIcon } from '#src/atoms/icons/GemIcon.tsx';
import { Shards } from '#src/atoms/icons/Shards.tsx';
import { FontAwesomeIcon } from '#src/atoms/icons/index.tsx';
import { Horizontal, Vertical } from '#src/atoms/layout/flex/index.tsx';
import { Tooltip } from '#src/atoms/tooltip/Tooltip/index.tsx';
import { BrandL, BrandS, LabelS } from '#src/atoms/typography/index.tsx';
import { DialogKey } from '#src/components/navigation/WithDialogs/index.tsx';
import {
  ANY_SPORT_ESSENCE,
  ANY_SPORT_ESSENCE_TYPE,
  MLB_MARKET_COMMON_MARKET,
} from '#src/constants/__generated__/routes.ts';
import { ANY_SPORT_MARKET_COMMON_PACKS } from '#src/constants/routes.ts';
import { useCurrentUserContext } from '#src/contexts/currentUser/index.ts';
import { useSportContext } from '#src/contexts/sport/index.ts';
import { useSeriesNav } from '#src/hooks/commonGame/useSeriesNav.ts';
import { usePinnedShardRarity } from '#src/hooks/config/usePinnedShardRarity.ts';
import { useIsLaptop } from '#src/hooks/device/useIsLaptop.ts';
import { useGetMyCardsBoostLink } from '#src/hooks/my-cards/useGetMyCardsBoostLink.tsx';
import { useOpenDialog } from '#src/hooks/navigation/useOpenDialog.ts';
import { useFeatureFlags } from '#src/hooks/useFeatureFlags.tsx';
import { LIFECYCLE, useLifecycle } from '#src/hooks/useLifecycle.ts';
import { formatScarcity } from '#src/lib/cards.ts';
import { getRarityColor } from '#src/lib/colors.ts';
import { useEvents } from '#src/lib/events/useEvents.ts';
import { glossary } from '#src/lib/glossary.ts';
import { inGameCurrencyToRarity } from '#src/lib/scarcity.ts';
import { OpenBuyGemsDialog_Context } from '#src/protos/events/so5/shared/events.ts';
import { laptopAndAbove } from '#src/style/mediaQuery.ts';
import type { Color } from '#src/style/types.ts';

import { PinButton } from './PinButton.tsx';
import { ResponsiveRaritiesWrapper } from './ResponsiveRaritiesWrapper.tsx';
import { ShardDropdownDetails } from './ShardDropdownDetails/index.tsx';

export enum InGameCurrencyAgreggated {
  ENERGY = 'ENERGY',
  XP = 'XP',
  SHARDS = 'SHARDS',
  BLUEPRINTS = 'BLUEPRINT',
  GEMS = 'GEMS',
}

type DetailsProps = { onClick?: () => void };

export type CurrencyData = {
  id: InGameCurrencyAgreggated;
  key: string;
  icon: ReactNode;
  amount?: number;
  details?: (props: DetailsProps) => ReactNode;
  visibleOnSports: Sport[];
  expanded?: boolean;
  onClick?: () => void;
};

const SelectedCurrencyWrapper = styled(Vertical).attrs({
  gap: 2,
})`
  position: relative;
  padding: var(--double-unit) var(--unit) var(--unit) var(--unit);
  border: 1px solid var(--c-nd-150);
  border-radius: var(--intermediate-unit);
  overflow: hidden;
`;

const RaritiesWrapper = styled(Horizontal)`
  align-items: stretch;
  @media ${laptopAndAbove} {
    flex-direction: column;
    gap: var(--half-unit);
  }
`;
const RarityWrapper = styled(Vertical)`
  justify-content: space-between;
  align-items: center;
  flex: 1;
  text-align: center;
  border-radius: var(--unit);
  padding: var(--unit) 0;
  background-color: var(--c-nd-50);

  @media ${laptopAndAbove} {
    flex-direction: row;
    width: 100%;
    padding: var(--half-unit) var(--unit);

    background-color: transparent;
    &:hover {
      background-color: var(--c-nd-50);
    }
  }
`;
const RarityContainer = styled(Vertical).attrs({ center: true })`
  width: 100%;
  align-items: stretch;
  @media ${laptopAndAbove} {
    flex-direction: row;
  }
`;

const messages = defineMessages({
  capAlmostReached: {
    id: 'useUserCurrencies.capAlmostReached',
    defaultMessage: 'Cap almost reached',
  },
});

const MIX_SHARD_COLOR: Color = 'var(--c-green-400)';
const MIX_XP_COLOR: Color = 'var(--c-rewards-blue)';

export const useUserCurrencies = (
  expandedCurrencies?: InGameCurrencyAgreggated[]
): CurrencyData[] => {
  const navigate = useNavigate();

  const { sport, generateSportContextPath } = useSportContext();
  const getMyCardsBoostLink = useGetMyCardsBoostLink();
  const track = useEvents();
  const { currentUser } = useCurrentUserContext();
  const {
    flags: { enableNavToggleProSet = false },
  } = useFeatureFlags();
  const [seeAllEnergy, setSeeAllEnergy] = useState(false);
  const { lifecycle, update: updateLifecycle } = useLifecycle();
  const { pinnedShardRarity, pinnedShardCardSet } = usePinnedShardRarity();
  const isLaptop = useIsLaptop();
  const seriesNav = useSeriesNav();
  const { energyBalance, xpBalances, cardShardsChests } = currentUser || {};

  const filteredXpBalances = xpBalances?.filter(balance =>
    lifecycle?.hideCommonFeatures
      ? balance.currency !== InGameCurrency.COMMON_XP
      : true
  );
  const totalEnergyBalance = energyBalance?.reduce(
    (sum, { currency, amount }) => {
      const rarity = inGameCurrencyToRarity[currency];
      if (!rarity) return sum;
      // do not show energy balance if user has no card of this rarity
      if (!seeAllEnergy && !currentUser?.cardCounts?.[rarity]) {
        return sum;
      }
      return sum + amount;
    },
    0
  );

  const { pinnedXpRarity } = lifecycle || {};

  const shardsAmount = pinnedShardRarity
    ? cardShardsChests?.find(
        ({ rarity, cardSet }) =>
          rarity === pinnedShardRarity && cardSet?.slug === pinnedShardCardSet
      )?.cardShardsCount || 0
    : cardShardsChests
        ?.filter(
          ({ rarity }) =>
            !lifecycle?.hideCommonFeatures || rarity !== Rarity.common
        )
        .reduce((sum, { cardShardsCount }) => sum + cardShardsCount, 0);
  const firstNonZeroShardsBalance = (cardShardsChests || []).find(
    ({ cardShardsCount }) => cardShardsCount > 0
  );
  const firstNonZeroShardsBalanceRarity = firstNonZeroShardsBalance?.rarity;

  const xpAmount = pinnedXpRarity
    ? filteredXpBalances?.find(
        ({ currency }) => inGameCurrencyToRarity[currency] === pinnedXpRarity
      )?.amount || 0
    : filteredXpBalances?.reduce((sum, { amount }) => sum + amount, 0);

  const firstNonZeroXPBalance = (filteredXpBalances || []).find(
    ({ amount }) => amount > 0
  );
  const firstNonZeroXPBalanceRarity = firstNonZeroXPBalance?.currency
    ? inGameCurrencyToRarity[firstNonZeroXPBalance.currency]
    : undefined;

  const gemsCount = currentUser?.gemsBalances[0]?.amount || 0;

  const pinCurrencyRarity = (rarity: Rarity) => {
    if (!lifecycle) {
      return;
    }
    updateLifecycle(
      LIFECYCLE.pinnedXpRarity,
      lifecycle?.[LIFECYCLE.pinnedXpRarity] === rarity ? false : rarity
    );
  };

  const openDialog = useOpenDialog();

  const openBuyGemsDialog = () => {
    openDialog(DialogKey.buyGems, 'true');
    track('Open Buy Gems Dialog', {
      context: OpenBuyGemsDialog_Context.INVENTORY,
    });
  };

  return [
    {
      id: InGameCurrencyAgreggated.SHARDS,
      key: `${InGameCurrencyAgreggated.SHARDS}_${pinnedShardRarity ?? 'all'}_${pinnedShardCardSet ?? 'all'}`,
      icon: (
        <Shards
          size={2}
          color={
            pinnedShardRarity
              ? getRarityColor(pinnedShardRarity, {
                  cardSet: pinnedShardCardSet,
                })
              : MIX_SHARD_COLOR
          }
        />
      ),
      amount: shardsAmount,
      details: ({ onClick }: DetailsProps) => (
        <ShardDropdownDetails onClick={onClick} />
      ),
      visibleOnSports: [Sport.FOOTBALL, Sport.NBA, Sport.BASEBALL],
      expanded: expandedCurrencies?.includes(InGameCurrencyAgreggated.SHARDS),
      onClick: () => {
        const rarity = pinnedShardRarity || firstNonZeroShardsBalanceRarity;
        if (rarity === Rarity.common) {
          navigate(
            sport === Sport.BASEBALL
              ? MLB_MARKET_COMMON_MARKET
              : generateSportContextPath(ANY_SPORT_MARKET_COMMON_PACKS)
          );
        } else if (rarity) {
          navigate(
            generateSportContextPath(ANY_SPORT_ESSENCE_TYPE, {
              params: { type: rarity },
            })
          );
        } else {
          navigate(generateSportContextPath(ANY_SPORT_ESSENCE));
        }
      },
    },
    {
      id: InGameCurrencyAgreggated.XP,
      key: InGameCurrencyAgreggated.XP,
      icon: (
        <Boost
          size={2}
          color={pinnedXpRarity ? getRarityColor(pinnedXpRarity) : MIX_XP_COLOR}
        />
      ),
      amount: xpAmount,
      details: ({ onClick }: DetailsProps) => (
        <SelectedCurrencyWrapper>
          <Vertical gap={0.5} center>
            <Boost
              size={2}
              color={
                pinnedXpRarity ? getRarityColor(pinnedXpRarity) : MIX_XP_COLOR
              }
            />
            <BrandS
              color={
                pinnedXpRarity ? getRarityColor(pinnedXpRarity) : MIX_XP_COLOR
              }
            >
              <FormattedMessage
                id="XpBalances.xpReserve"
                defaultMessage="XP Reserve"
              />
            </BrandS>
            <BrandL>
              <FormattedNumber value={xpAmount || 0} />
            </BrandL>
          </Vertical>
          <ResponsiveRaritiesWrapper>
            {filteredXpBalances?.map(
              ({ currency, amount, capAlmostReached }) => {
                const rarity = inGameCurrencyToRarity[currency];
                if (!rarity) return null;
                return (
                  <RarityContainer key={currency}>
                    {!!isLaptop && (
                      <PinButton
                        onClick={() => pinCurrencyRarity(rarity)}
                        isPinned={pinnedXpRarity === rarity}
                      />
                    )}
                    <RarityWrapper
                      as={Link}
                      to={getMyCardsBoostLink({
                        rarity,
                        sport,
                      })}
                      onClick={onClick}
                    >
                      <LabelS color={'var(--c-nd-600)'}>
                        {formatScarcity(rarity)}
                      </LabelS>
                      <Horizontal>
                        <BrandS>
                          <FormattedNumber value={amount} />
                        </BrandS>
                        {!!capAlmostReached && (
                          <Tooltip
                            title={
                              <FormattedMessage
                                {...messages.capAlmostReached}
                              />
                            }
                          >
                            <FontAwesomeIcon
                              size="xs"
                              color="var(--c-yellow-400)"
                              icon={faTriangleExclamation}
                            />
                          </Tooltip>
                        )}
                      </Horizontal>
                    </RarityWrapper>
                    {!isLaptop && (
                      <PinButton
                        onClick={() => pinCurrencyRarity(rarity)}
                        isPinned={pinnedXpRarity === rarity}
                      />
                    )}
                  </RarityContainer>
                );
              }
            )}
          </ResponsiveRaritiesWrapper>
        </SelectedCurrencyWrapper>
      ),
      visibleOnSports: [Sport.FOOTBALL, Sport.NBA, Sport.BASEBALL],
      expanded: expandedCurrencies?.includes(InGameCurrencyAgreggated.XP),
      onClick: () => {
        const rarity = lifecycle?.pinnedXpRarity || firstNonZeroXPBalanceRarity;
        if (rarity) {
          navigate(getMyCardsBoostLink({ rarity, sport }));
        } else {
          navigate(
            sport === Sport.BASEBALL
              ? MLB_MARKET_COMMON_MARKET
              : generateSportContextPath(ANY_SPORT_MARKET_COMMON_PACKS)
          );
        }
      },
    },
    {
      id: InGameCurrencyAgreggated.ENERGY,
      key: InGameCurrencyAgreggated.ENERGY,
      icon: <Energy size={2} color="var(--c-yellow-300)" />,
      amount: totalEnergyBalance,
      details: () => (
        <SelectedCurrencyWrapper>
          <Vertical gap={0.5} center>
            <Energy size={2} color="var(--c-yellow-300)" />
            <BrandS color="var(--c-yellow-300)">
              <FormattedMessage
                id="useUserCurrencies.energy"
                defaultMessage="Energy"
              />
            </BrandS>
            <BrandL>
              <FormattedNumber value={totalEnergyBalance || 0} />
            </BrandL>
          </Vertical>
          <RaritiesWrapper gap={0}>
            {energyBalance?.map(({ currency, amount, capAlmostReached }) => {
              const rarity = inGameCurrencyToRarity[currency];
              if (!rarity) return null;
              // do not show energy balance if user has no card of this rarity
              if (!seeAllEnergy && !currentUser?.cardCounts?.[rarity]) {
                return null;
              }
              return (
                <RarityWrapper key={currency}>
                  <LabelS>{formatScarcity(rarity)}</LabelS>
                  <Horizontal>
                    <BrandS>
                      <FormattedNumber value={amount} />
                    </BrandS>
                    {!!capAlmostReached && (
                      <Tooltip
                        title={
                          <FormattedMessage {...messages.capAlmostReached} />
                        }
                      >
                        <FontAwesomeIcon
                          size="xs"
                          color="var(--c-yellow-400)"
                          icon={faTriangleExclamation}
                        />
                      </Tooltip>
                    )}
                  </Horizontal>
                </RarityWrapper>
              );
            })}
          </RaritiesWrapper>
          <Button
            size="compact"
            fullWidth
            color="quaternary"
            disableDebounce
            onClick={() => setSeeAllEnergy(prev => !prev)}
          >
            <FormattedMessage
              {...(seeAllEnergy ? glossary.showLess : glossary.showMore)}
            />
          </Button>
        </SelectedCurrencyWrapper>
      ),
      visibleOnSports: [Sport.BASEBALL],
      expanded: expandedCurrencies?.includes(InGameCurrencyAgreggated.ENERGY),
    },
    {
      id: InGameCurrencyAgreggated.GEMS,
      key: InGameCurrencyAgreggated.GEMS,
      icon: <GemIcon size={2} />,
      amount: gemsCount,
      details: ({ onClick }: DetailsProps) => (
        <SelectedCurrencyWrapper>
          <Vertical gap={0.5} center>
            <GemIcon size={2} />
            <BrandS color="var(--c-green-400)">
              <FormattedMessage
                id="useUserCurrencies.gems"
                defaultMessage="Gems"
              />
            </BrandS>
            <BrandL>
              <FormattedNumber value={gemsCount || 0} />
            </BrandL>
          </Vertical>
          <Button
            size="medium"
            fullWidth
            color="quaternary"
            onClick={() => {
              openBuyGemsDialog();
              onClick?.();
            }}
          >
            <FormattedMessage
              id="useUserCurrencies.getGems"
              defaultMessage="Get Gems"
            />
          </Button>
        </SelectedCurrencyWrapper>
      ),
      onClick: openBuyGemsDialog,
      visibleOnSports: [Sport.FOOTBALL, Sport.NBA],
      expanded: expandedCurrencies?.includes(InGameCurrencyAgreggated.GEMS),
    },
  ]
    .filter(
      currency => currency.id !== InGameCurrencyAgreggated.XP || !seriesNav
    )
    .filter(
      currency =>
        currency.id !== InGameCurrencyAgreggated.GEMS ||
        !(
          enableNavToggleProSet && currentUser?.anyUserSportProfile?.proUnlocked
        )
    )
    .filter(Boolean);
};
